import { memo } from "react";
import { markerUrl } from "@/data/markers.ts";
import { gameToScreen, type Camera, type ProjectionConfig } from "@/engine/projection.ts";
import type { Marker } from "@/session/schemas.ts";

interface MarkerLayerProps {
  readonly markers: readonly Marker[];
  readonly camera: Camera;
  readonly config: ProjectionConfig;
  readonly myId: string;
  /** The commander can clear anyone's marker; a player can only clear their own. */
  readonly canManageAll: boolean;
  readonly onRemove: (markerId: string) => void;
}

/** Rendered icon size in screen pixels. Source art is 32x32. */
const ICON_SIZE = 22;

/**
 * Player-placed markers: contacts, hazards, vehicles, whatever somebody
 * dropped a pin on. Unlike an order this is not addressed to anyone — it is
 * just something worth every squad seeing, the same way a callout over voice
 * comms is for whoever is listening.
 *
 * Removing one is a tap on the icon itself, not a separate little button
 * bolted on next to it — a marker was one tap to place, so it is one tap to
 * clear, on the same spot. The hit area is padded well past the visible
 * icon, since the icon alone is a poor target on a tablet.
 */
function MarkerLayerImpl({
  markers,
  camera,
  config,
  myId,
  canManageAll,
  onRemove,
}: MarkerLayerProps): React.ReactElement {
  return (
    <svg className="pointer-events-none absolute inset-0 h-full w-full">
      {markers.map((marker) => {
        const at = gameToScreen(marker.at, camera, config);
        const half = ICON_SIZE / 2;
        const canRemove = canManageAll || marker.createdBy === myId;

        return (
          <g
            key={marker.id}
            className={canRemove ? "pointer-events-auto cursor-pointer" : undefined}
            // `data-no-pan` tells the map's pan gesture (see `useCamera`) to
            // keep its hands off a press that starts on this marker, so the
            // click that removes it is not swallowed by pointer capture on
            // the container.
            data-no-pan={canRemove ? "" : undefined}
            onClick={
              canRemove
                ? (event) => {
                    event.stopPropagation();
                    onRemove(marker.id);
                  }
                : undefined
            }
            role={canRemove ? "button" : undefined}
            aria-label={canRemove ? "Remove marker" : undefined}
          >
            {canRemove && <title>Click to remove</title>}

            {/* Generous invisible hit area: the icon itself is small, and a
                tap that misses by a few pixels should still register. */}
            {canRemove && <circle cx={at.x} cy={at.y} r={half + 10} fill="transparent" />}

            {/* A dark disc behind the icon so it survives snow and sand,
                matching the game's own POIs. Threats get a red outline so
                they read differently at a glance. */}
            <circle
              cx={at.x}
              cy={at.y}
              r={half + 3}
              fill={
                marker.threat ? "oklch(0.18 0.05 25 / 0.8)" : "oklch(0.12 0.008 250 / 0.72)"
              }
              stroke={marker.threat ? "var(--color-danger)" : "oklch(0.95 0.005 250 / 0.28)"}
              strokeWidth={marker.threat ? 1.5 : 1}
            />
            <image
              href={markerUrl(marker.kind)}
              x={at.x - half}
              y={at.y - half}
              width={ICON_SIZE}
              height={ICON_SIZE}
              preserveAspectRatio="xMidYMid meet"
            />
          </g>
        );
      })}
    </svg>
  );
}

export const MarkerLayer = memo(MarkerLayerImpl);
